import { useEffect, useRef, useState } from "react";
import type { CheckoutSocketEvent } from "@cx/contracts";
import { checkoutSocketUrl, reconnectDelay, throttle } from "./labels";

/** Recarga a cada evento, no máximo uma a cada 2 s (rajada de eventos não vira rajada de requisições). */
export const LIVE_REFRESH_MS = 2_000;

const PING_MS = 30_000;
export type SocketStatus = "connecting" | "live" | "offline";

/** Função estável que chama a mais recente `fn` com throttle; descarta a pendente ao desmontar. */
export function useThrottled(fn: () => void, ms = LIVE_REFRESH_MS): () => void {
  const latest = useRef(fn);
  useEffect(() => {
    latest.current = fn;
  });
  const [t] = useState(() => throttle(() => latest.current(), ms));
  useEffect(() => () => t.cancel(), [t]);
  return t;
}

/**
 * Atualizações ao vivo do checkout. O servidor manda só ids e estado; quem usa relê pela API. Sem `sessionId` (fila)
 * chegam só as mudanças de estado (abrir, reiniciar, salvar, cancelar); com ele (tela da conferência), também as
 * bipagens daquela sessão. Reconecta com espera crescente (o 4001 de conexão vencida ou de logout reconecta na hora e
 * passa pela autenticação de novo); "ping" a cada 30 s mantém a conexão (respondido sem acordar o servidor).
 */
export function useCheckoutSocket(
  companyId: string,
  onEvent: (e: CheckoutSocketEvent) => void,
  sessionId?: string,
): SocketStatus {
  const [status, setStatus] = useState<SocketStatus>("connecting");
  const handler = useRef(onEvent);
  useEffect(() => {
    handler.current = onEvent;
  });
  useEffect(() => {
    let ws: WebSocket | null = null;
    let attempt = 0;
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;
    const connect = () => {
      setStatus("connecting");
      ws = new WebSocket(checkoutSocketUrl(window.location, companyId, sessionId));
      ws.onopen = () => {
        attempt = 0;
        setStatus("live");
        ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send("ping"), PING_MS);
      };
      ws.onmessage = (m) => {
        if (m.data === "pong") return;
        try {
          handler.current(JSON.parse(String(m.data)) as CheckoutSocketEvent);
        } catch {
          // Mensagem fora do formato: ignora.
        }
      };
      ws.onclose = (c) => {
        clearInterval(ping);
        if (stopped) return;
        setStatus("offline");
        retry = setTimeout(connect, c.code === 4001 ? 0 : reconnectDelay(attempt++));
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(retry);
      clearInterval(ping);
      ws?.close(1000, "fim");
    };
  }, [companyId, sessionId]);
  return status;
}

export const SOCKET_STATUS_LABEL: Record<SocketStatus, string> = {
  connecting: "Conectando…",
  live: "Ao vivo",
  offline: "Sem conexão ao vivo: tentando de novo",
};
